import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Compass, Handshake, MessageSquare, Plus, User, Users, ChevronDown, ShieldCheck, LogOut } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { IncomingCall } from "@/components/call/CallButton";
import { Avatar } from "@/components/common";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/connect", label: "Connect", icon: Handshake, id: "connect" },
  { to: "/discover", label: "Discover", icon: Compass, id: "discover" },
  { to: "/circle", label: "My circle", icon: Users, id: "circle" },
  { to: "/messages", label: "Messages", icon: MessageSquare, id: "messages" },
];

function useUnread() {
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/threads").then(({ data }) => alive && setUnread(data.filter((t) => t.unread).length)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [loc.pathname]);
  return unread;
}

const UnreadDot = ({ n }) => n > 0 ? <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground" data-testid="unread-badge">{n}</span> : null;

const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex min-h-[44px] items-center gap-2 rounded-full" data-testid="account-menu-trigger" aria-label="Account menu">
        <Avatar person={user} size={44} className="border-foreground/30" />
        <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 border-border bg-card">
        <div className="px-2 py-2 text-sm"><p className="font-semibold">{user.name}</p><p className="truncate text-muted-foreground">{user.email}</p></div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/profile")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="menu-admin"><ShieldCheck className="mr-2 h-4 w-4" /> Applications</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={async () => { await logout(); navigate("/"); }} data-testid="menu-signout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function MemberShell() {
  const unread = useUnread();
  return (
    <div className="min-h-screen pb-[84px] lg:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl" data-testid="member-header">
        <div className="shell flex h-[70px] items-center justify-between gap-6">
          <Logo to="/connect" />
          <nav aria-label="Members" className="hidden items-center gap-12 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.id} to={n.to} data-testid={`member-nav-${n.id}`}
                className={({ isActive }) => cn("relative py-2 text-[15px] transition-colors hover:text-primary", isActive && "font-semibold after:absolute after:inset-x-0 after:-bottom-1 after:h-[2px] after:bg-primary")}>
                {n.label}{n.id === "messages" && <UnreadDot n={unread} />}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/experiences/new" className="btn-primary hidden min-h-[44px] px-5 sm:inline-flex" data-testid="create-experience-button"><Plus className="h-4 w-4" /> Create experience</Link>
            <Link to="/experiences/new" className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground sm:hidden" aria-label="Create experience" data-testid="create-experience-button-mobile"><Plus className="h-5 w-5" /></Link>
            <AccountMenu />
          </div>
        </div>
      </header>
      <main><Outlet />
      <IncomingCall /></main>
      <nav aria-label="Members mobile" className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl lg:hidden" data-testid="bottom-nav">
        <div className="grid grid-cols-5">
          {[...NAV, { to: "/profile", label: "Profile", icon: User, id: "profile" }].map((n) => (
            <NavLink key={n.id} to={n.to} data-testid={`bottom-nav-${n.id}`}
              className={({ isActive }) => cn("flex min-h-[64px] flex-col items-center justify-center gap-1 text-xs", isActive ? "text-primary" : "text-foreground/80")}>
              <span className="relative"><n.icon className="h-6 w-6" strokeWidth={1.5} />{n.id === "messages" && <UnreadDot n={unread} />}</span>
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
